(ns arthur.domain.history
  "Undo, per person, as leaf writes. docs/architecture.md, \"Undo is per-user\".

  A step is the leaves one edit changed: what they held before, and what they
  held after. Undoing writes the befores back as an ordinary edit, which the
  next save sends like any other — so undo needs nothing from the server, and
  nothing about it is shared.

  ONLY YOUR OWN CHANGES. A step undoes only if every leaf it touched still holds
  what the step left there. Somebody else's write to one of them since — their
  edit to the shape you made — refuses the step rather than taking their work
  with it; it is dropped, and the next undo is the step before. With nobody else
  in the document the values always match, and this is ordinary undo.

  A nil value is an absent leaf: a step that made a node has nil befores for its
  leaves, so undoing it removes them."
  (:require [clojure.string :as str]))

(def gap-ms
  "Edits to the same leaves closer together than this are one step: a drag
  writes a vertex per pointermove, and is one thing to undo. Only when each
  starts where the last left off — anything landing between them, a
  collaborator's write included, makes the next edit a step of its own."
  1000)

(def depth 200)

(defn- changes
  "`[before after]`, restricted to the paths that differ."
  [before after]
  (reduce (fn [[b a :as acc] path]
            (let [x (get before path)
                  y (get after path)]
              (if (= x y) acc [(assoc b path x) (assoc a path y)])))
          [{} {}]
          (distinct (concat (keys before) (keys after)))))

(defn- node-name [leaves path]
  (let [[_ _ _ sid _ nid] (str/split path #"/")
        node (get leaves (str/join "/" ["clip" "u" "symbol" sid "node" nid]))]
    (or (:name node) (str/replace nid "~" "/"))))

(defn- said
  "What one changed leaf was, in words, and how much it outranks the others:
  making or deleting a thing names the step before editing it does."
  [before after path]
  (let [[_ _ kind a b] (str/split path #"/")
        leaves (merge before after)]
    (case [kind b]
      ["symbol" nil]  [1 (str "symbol " (or (:name (get leaves path)) (str/replace a "~" "/")))]
      ["symbol" "node"]
      (cond (nil? (get before path)) [0 (str "add " (node-name leaves path))]
            (nil? (get after path))  [0 (str "delete " (node-name leaves path))]
            :else                    [1 (str "edit " (node-name leaves path))])
      (if (#{"channel" "measured"} b)
        [1 (str "edit " (node-name leaves path))]
        [2 (case kind
             ("timing" "stage" "name") "project settings"
             ("subject" "feature" "group") "tracking settings"
             kind)]))))

(defn label
  "A step in words: \"add shape 3\", \"edit mouth, brow-l\"."
  [before after paths]
  (let [said (->> paths (map #(said before after %)) distinct sort)
        top  (first (first said))
        words (distinct (map second (filter #(= top (first %)) said)))]
    (str (str/join ", " (take 2 words)) (when (< 2 (count words)) " …"))))

(defn record
  "History `h` with an edit from leaves `before` to `after` at time `now`."
  [{:keys [done held?] :as h} before after now]
  (let [[b a] (changes before after)
        top   (peek done)]
    (cond
      (empty? a) h
      (and top (not (:closed? top)) (= b (:after top))
           (or held? (< (- now (:at top)) gap-ms)))
      (assoc h :done (conj (pop done) (assoc top :after a :at now)) :undone [])
      :else
      (assoc h
             :done (conj (vec (take-last (dec depth) done))
                         {:before b :after a :at now :label (label before after (keys a))})
             :undone []))))

(defn- close [{:keys [done] :as h}]
  (cond-> h (seq done) (assoc :done (conj (pop done) (assoc (peek done) :closed? true)))))

(defn hold
  "While a field has focus, everything typed into it is one step, however slowly
  — the digits of 45 are seen as 4 and then 45, and undone as one. It starts a
  step of its own rather than joining whatever came before."
  [h]
  (assoc (close h) :held? true))

(defn settle
  "The field is done with: its step is finished, and nothing joins it."
  [h]
  (dissoc (close h) :held?))

(defn steps
  "The labels, newest first: `:done` is what undo would take off, `:undone`
  what redo would put back."
  [h]
  {:done   (mapv :label (rseq (or (:done h) [])))
   :undone (mapv :label (rseq (or (:undone h) [])))})

(defn- holds? [leaves m]
  (every? (fn [[path v]] (= v (get leaves path))) m))

(defn- put-all [leaves m]
  (reduce-kv (fn [ls path v] (if (nil? v) (dissoc ls path) (assoc ls path v))) leaves m))

(defn- move
  "One step from `from` to `to`, if `leaves` still hold what it expects."
  [h leaves from to expect write]
  (when-let [step (peek (get h from))]
    (let [h (update h from pop)]
      (if (holds? leaves (expect step))
        {:leaves (put-all leaves (write step)) :history (update h to (fnil conj []) step)}
        {:blocked step :history h}))))

(defn undo
  "`{:leaves :history}`, `{:blocked :history}` when somebody else has since
  changed what the step touched, or nil with nothing to undo."
  [h leaves]
  (move h leaves :done :undone :after :before))

(defn redo [h leaves]
  (move h leaves :undone :done :before :after))
